import { BaseRepository } from './base.repository';

export interface VariacaoItem {
  id: number;
  nome: string;
  exames_count: number;
}

export interface ExameClinicoItem {
  id: number;
  nome: string;
  obrigatorio: boolean;
  preco: number;
  laboratorial: boolean;
  variacao_exame_clinico_id: number;
}

export interface ExameItem {
  id: number;
  nome: string;
  descricao?: string;
  preco?: number;
}

/**
 * Repository para operações relacionadas a exames
 */
export class ExamesRepository extends BaseRepository {
  /**
   * Lista todas as variações (atividades complementares) disponíveis
   */
  async getVariacoes(): Promise<VariacaoItem[]> {
    return this.get<VariacaoItem[]>('exames/variacoes');
  }

  /**
   * Lista exames clínicos disponíveis para uma variação e clínica
   */
  async getExamesClinicosPorVariacaoClinica(
    variacaoId: number,
    clinicaId: number
  ): Promise<ExameClinicoItem[]> {
    return this.get<ExameClinicoItem[]>(
      `exames/exames-clinicos/variacao/${variacaoId}/clinica/${clinicaId}`
    );
  }

  /**
   * Lista exames disponíveis para agendamento
   */
  async getExamesDisponiveis(): Promise<ExameItem[]> {
    return this.get<ExameItem[]>('exames');
  }

  /**
   * Lista exames vinculados a um colaborador
   * @param colaboradorId - ID do colaborador
   * @param empresaId - ID da empresa (fallback se pedidoId não estiver disponível)
   * @param pedidoId - ID do pedido (prioridade - mais específico, evita dados indevidos)
   */
  async getExamesPorColaborador(
    colaboradorId: number,
    empresaId?: number,
    pedidoId?: number
  ): Promise<any[]> {
    const params = new URLSearchParams();
    if (pedidoId) {
      params.append('pedidoId', pedidoId.toString());
    } else if (empresaId) {
      params.append('empresaId', empresaId.toString());
    }
    const queryString = params.toString();
    return this.get<any[]>(
      `exames/colaborador/${colaboradorId}${queryString ? `?${queryString}` : ''}`
    );
  }

  /**
   * Vincula exame a um colaborador
   */
  async vincularExameColaborador(
    colaboradorId: number,
    exameId: number,
    clinicaId?: number
  ): Promise<any> {
    return this.post(`exames/colaborador/${colaboradorId}`, {
      exame_id: exameId,
      clinica_id: clinicaId,
    });
  }

  /**
   * Desvincula exame de um colaborador
   */
  async desvincularExameColaborador(
    colaboradorId: number,
    exameId: number
  ): Promise<void> {
    return this.delete(`exames/colaborador/${colaboradorId}/exame/${exameId}`);
  }

  /**
   * Vincula/desvincula exame clínico a um colaborador
   */
  async vincularExameClinicoColaborador(
    colaboradorId: number,
    exameClinicoId: number,
    variacaoId: number,
    clinicaId: number | undefined,
    vincular: boolean,
    empresaId?: number
  ): Promise<any> {
    // Obter empresaId do localStorage se não foi fornecido
    let finalEmpresaId = empresaId
    if (!finalEmpresaId) {
      try {
        const empresaDataStr = localStorage.getItem('empresaData')
        if (empresaDataStr) {
          const empresaData = JSON.parse(empresaDataStr)
          finalEmpresaId = empresaData.id
        }
      } catch (error) {
        console.warn('⚠️ Erro ao obter empresaId do localStorage:', error)
      }
    }

    if (!finalEmpresaId) {
      throw new Error('empresaId é obrigatório para vincular exame clínico')
    }

    return this.post(
      `exames/colaborador/${colaboradorId}/exame-clinico`,
      {
        exame_clinico_id: exameClinicoId,
        variacao_id: variacaoId,
        clinica_id: clinicaId,
        vincular,
        empresaId: finalEmpresaId,
      }
    );
  }

  /**
   * Calcula preço total dos exames de um colaborador
   */
  async calcularPrecoColaborador(colaboradorId: number): Promise<any> {
    return this.get<any>(`exames/colaborador/${colaboradorId}/preco`);
  }

  /**
   * Calcula preço total de todos os exames da empresa
   */
  async calcularPrecoTotalEmpresa(): Promise<any> {
    return this.get<any>('exames/empresa/preco-total');
  }

  /**
   * Busca o preço de um exame clínico em uma clínica específica.
   * Usado para normalização de preços.
   * @param exameClinicoId - ID do exame clínico
   * @param clinicaId - ID da clínica
   * @returns Preço do exame ou null se não encontrado
   */
  async getPrecoExameClinica(exameClinicoId: number, clinicaId: number): Promise<{ preco: number | null; nome?: string }> {
    return this.get<{ preco: number | null; nome?: string }>(
      `exames/exames-clinicos/${exameClinicoId}/preco/clinica/${clinicaId}`
    );
  }

  /**
   * Busca o preço base de um exame clínico (qualquer clínica).
   * Usado como fallback quando não encontra preço em Santana.
   * @param exameClinicoId - ID do exame clínico
   * @returns Preço base do exame ou null se não encontrado
   */
  async getPrecoBaseExameClinico(exameClinicoId: number): Promise<{ preco: number | null; nome?: string }> {
    return this.get<{ preco: number | null; nome?: string }>(
      `exames/exames-clinicos/${exameClinicoId}/preco-base`
    );
  }

  /**
   * Atualiza data_agendamento e/ou clinica_id de um agendamento
   * Só permite atualizar agendamentos sem pedido_id
   * @param agendamentoId - ID do agendamento
   * @param updateData - Dados para atualizar
   * @returns Agendamento atualizado
   */
  async updateAgendamento(
    agendamentoId: number,
    updateData: {
      data_agendamento?: string;
      clinica_id?: number;
    }
  ): Promise<any> {
    return this.patch<any>(
      `exames/agendamentos/${agendamentoId}`,
      updateData
    );
  }
}
